import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  ChevronLeft,
  ChevronRight,
  Home,
  FileText,
  Upload,
  Trash2,
  Download,
  Loader2,
  Plus,
  X,
  AlertTriangle,
  CheckCircle,
  Info,
  Scale,
  Calendar,
  User,
  Building2,
  ClipboardList,
  Brain,
  MessageSquare,
  Send,
  Clock,
  Search,
  FileDown,
  Sparkles,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { num: 1, label: 'Case Intake', icon: ClipboardList },
  { num: 2, label: 'Upload Documents', icon: Upload },
  { num: 3, label: 'AI Case Analysis', icon: Brain },
  { num: 4, label: 'Document Generator', icon: FileDown },
  { num: 5, label: 'AI Attorney', icon: MessageSquare },
  { num: 6, label: 'Dashboard', icon: Home },
];

const CASE_STAGES = [
  'Behind on payments',
  'Default notice received',
  'Acceleration notice',
  'Foreclosure scheduled',
  'Mediation',
  'Lawsuit pending',
  'Property sold',
  'Eviction pending',
  'Other',
];

const DOC_CATEGORIES = [
  'Mortgage', 'Deed of Trust', 'Note', 'Payment History', 'Monthly Statement',
  'Default Notice', 'Acceleration Notice', 'Trustee Notice', 'Correspondence',
  'Court Filing', 'Recorded Document', 'Email', 'Certified Mail Receipt', 'Other',
];

const QUIET_TITLE_DOC_TITLE = 'Petition to Quiet Title';
const FORECLOSURE_DOC_TITLE = 'Petition to Produce the Original Promissory Note, Conditional Acceptance for Value, and Demand for Full Accounting';

const ANALYSIS_TABS = [
  { key: 'full', label: 'Full Analysis', icon: Sparkles },
  { key: 'summary', label: 'Executive Summary', icon: FileText },
  { key: 'mortgage', label: 'Mortgage Breakdown', icon: Home },
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'issues', label: 'Issue Spotter', icon: Search },
  { key: 'equity', label: 'Equity Analysis', icon: Scale },
];

export default function ForeclosureDefender() {
  const { toast } = useToast();
  const [cases, setCases] = useState([]);
  const [activeCase, setActiveCase] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Step 1: Intake
  const [property, setProperty] = useState({ address: '', loan_origination_date: '', loan_status: '', occupancy: 'Primary residence' });
  const [parties, setParties] = useState({ borrower: '', servicer: '', lender: '', trustee: '', substitute_trustee: '', law_firm: '', collection_companies: '' });
  const [caseStage, setCaseStage] = useState('');

  // Step 2: Documents
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('Other');

  // Step 3: Analysis
  const [analysisTab, setAnalysisTab] = useState('full');
  const [analysisResults, setAnalysisResults] = useState({});
  const [analyzing, setAnalyzing] = useState(false);

  // Step 4: Doc Generator (now before AI Attorney)
  const [docGenTab, setDocGenTab] = useState('petition'); // 'petition' | 'quiet_title'
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [generatingQT, setGeneratingQT] = useState(false);
  const [generatedQT, setGeneratedQT] = useState(null);

  // Step 5: Chat (was step 4)
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Step 6: Audit
  const [auditTrail, setAuditTrail] = useState([]);

  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCases = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/foreclosure/cases`, { headers });
      setCases(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast({ title: 'Subscription Required', description: 'Foreclosure Defender requires a paid plan (Justice Seeker or above).', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const loadCase = async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/foreclosure/cases/${id}`, { headers });
      const c = res.data;
      setActiveCase(c);
      setProperty(c.property || { address: '', loan_origination_date: '', loan_status: '', occupancy: 'Primary residence' });
      setParties(c.parties || { borrower: '', servicer: '', lender: '', trustee: '', substitute_trustee: '', law_firm: '', collection_companies: '' });
      setCaseStage(c.case_stage || '');
      setDocuments(c.documents || []);
      setAuditTrail(c.audit_trail || []);
      setChatHistory(c.chat_history || []);
      // Load analysis results
      const results = {};
      if (c.analysis) results.full = c.analysis;
      ['summary', 'mortgage', 'timeline', 'issues', 'equity'].forEach(t => {
        if (c[`analysis_${t}`]) results[t] = c[`analysis_${t}`];
      });
      setAnalysisResults(results);
      setGeneratedDoc(null);
      setChatMessage('');
      setCurrentStep(1);
    } catch {
      toast({ title: 'Error', description: 'Failed to load case.', variant: 'destructive' });
    }
  };

  const createNew = async () => {
    try {
      const res = await axios.post(`${API_BASE}/foreclosure/cases`, {}, { headers });
      await fetchCases();
      loadCase(res.data.id);
      toast({ title: 'New Case Created', description: 'Start by entering your property information.' });
    } catch (err) {
      if (err.response?.status === 403) {
        toast({ title: 'Subscription Required', description: 'Upgrade to Justice Seeker or above.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: 'Failed to create case.', variant: 'destructive' });
      }
    }
  };

  const saveIntake = async () => {
    if (!activeCase) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/foreclosure/cases/${activeCase.id}`, {
        property, parties, case_stage: caseStage,
      }, { headers });
      setActiveCase(res.data);
      toast({ title: 'Saved', description: 'Case details saved.' });
    } catch {
      toast({ title: 'Save Failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (currentStep === 1) await saveIntake();
    setCurrentStep(s => Math.min(s + 1, 6));
  };
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeCase) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadCategory);
      formData.append('description', '');
      await axios.post(`${API_BASE}/foreclosure/cases/${activeCase.id}/documents`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const updated = await axios.get(`${API_BASE}/foreclosure/cases/${activeCase.id}`, { headers });
      setDocuments(updated.data.documents || []);
      toast({ title: 'Document Uploaded', description: `${file.name} added.` });
    } catch {
      toast({ title: 'Upload Failed', variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API_BASE}/foreclosure/cases/${activeCase.id}/documents/${docId}`, { headers });
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast({ title: 'Document Removed' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const deleteCase = async (id) => {
    try {
      await axios.delete(`${API_BASE}/foreclosure/cases/${id}`, { headers });
      if (activeCase?.id === id) { setActiveCase(null); setCurrentStep(1); }
      await fetchCases();
      toast({ title: 'Case Deleted' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const runAnalysis = async (type) => {
    if (!activeCase) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API_BASE}/foreclosure/cases/${activeCase.id}/analyze`, { analysis_type: type }, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        let attempts = 0;
        const poll = async () => {
          if (attempts >= 60) { setAnalyzing(false); toast({ title: 'Timeout', description: 'Analysis took too long.', variant: 'destructive' }); return; }
          attempts++;
          try {
            const jr = await axios.get(`${API_BASE}/foreclosure/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setAnalysisResults(prev => ({ ...prev, [type]: jr.data.result }));
              setAnalyzing(false);
              toast({ title: 'Analysis Complete' });
              // Refresh case data
              const updated = await axios.get(`${API_BASE}/foreclosure/cases/${activeCase.id}`, { headers });
              setAuditTrail(updated.data.audit_trail || []);
            } else if (jr.data.status === 'failed') {
              setAnalyzing(false);
              toast({ title: 'Analysis Failed', description: jr.data.error, variant: 'destructive' });
            } else {
              setTimeout(poll, 3000);
            }
          } catch { setTimeout(poll, 3000); }
        };
        poll();
      }
    } catch {
      setAnalyzing(false);
      toast({ title: 'Error', description: 'Failed to start analysis.', variant: 'destructive' });
    }
  };

  const sendChat = async () => {
    if ((!chatMessage.trim() && !pendingFile) || !activeCase) return;
    const msg = chatMessage.trim() || (pendingFile ? `Please analyze the attached document: ${pendingFile.filename}` : '');
    const fileCtx = pendingFile;
    const displayMsg = pendingFile ? `[Attached: ${pendingFile.filename}] ${msg}` : msg;
    setChatMessage('');
    setPendingFile(null);
    setChatLoading(true);
    setChatHistory(prev => [...prev, { user: displayMsg, assistant: null, timestamp: new Date().toISOString() }]);
    try {
      const payload = { message: msg };
      if (fileCtx) payload.file_context = fileCtx.text;
      const res = await axios.post(`${API_BASE}/foreclosure/cases/${activeCase.id}/chat`, payload, { headers });
      setChatHistory(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { user: displayMsg, assistant: res.data.response, timestamp: new Date().toISOString() };
        return updated;
      });
    } catch (err) {
      setChatHistory(prev => prev.slice(0, -1));
      toast({ title: 'Error', description: err.response?.data?.detail || 'Chat failed.', variant: 'destructive' });
    } finally {
      setChatLoading(false);
    }
  };

  const generateDocument = async () => {
    if (!activeCase) return;
    setGeneratingDoc(true);
    try {
      const res = await axios.post(`${API_BASE}/foreclosure/cases/${activeCase.id}/generate-document`, {}, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        let attempts = 0;
        const poll = async () => {
          if (attempts >= 80) { setGeneratingDoc(false); toast({ title: 'Timeout', variant: 'destructive' }); return; }
          attempts++;
          try {
            const jr = await axios.get(`${API_BASE}/foreclosure/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setGeneratedDoc(jr.data.document);
              setGeneratingDoc(false);
              toast({ title: 'Document Generated', description: 'Your foreclosure petition is ready for download.' });
            } else if (jr.data.status === 'failed') {
              setGeneratingDoc(false);
              toast({ title: 'Generation Failed', description: jr.data.error, variant: 'destructive' });
            } else {
              setTimeout(poll, 3000);
            }
          } catch { setTimeout(poll, 3000); }
        };
        poll();
      }
    } catch {
      setGeneratingDoc(false);
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const generateQuietTitle = async () => {
    if (!activeCase) return;
    setGeneratingQT(true);
    try {
      const res = await axios.post(`${API_BASE}/foreclosure/cases/${activeCase.id}/generate-quiet-title`, {}, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        let attempts = 0;
        const poll = async () => {
          if (attempts >= 80) { setGeneratingQT(false); toast({ title: 'Timeout', variant: 'destructive' }); return; }
          attempts++;
          try {
            const jr = await axios.get(`${API_BASE}/foreclosure/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setGeneratedQT(jr.data.document);
              setGeneratingQT(false);
              toast({ title: 'Document Generated', description: 'Your Petition to Quiet Title is ready for download.' });
            } else if (jr.data.status === 'failed') {
              setGeneratingQT(false);
              toast({ title: 'Generation Failed', description: jr.data.error, variant: 'destructive' });
            } else {
              setTimeout(poll, 3000);
            }
          } catch { setTimeout(poll, 3000); }
        };
        poll();
      }
    } catch {
      setGeneratingQT(false);
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const downloadDoc = (doc) => {
    const byteChars = atob(doc.content);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNums)], { type: doc.mime_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── RENDER STEPS ───

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-emerald-900">Case Intake Wizard</h3>
            <p className="text-sm text-emerald-700 mt-1">
              Enter your property details, identify all parties involved, and select your current case stage. This information helps the AI analyze your situation.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Home className="w-4 h-4" /> Property Information</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Property Address</label>
            <Input value={property.address || ''} onChange={e => setProperty(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St, City, State ZIP" data-testid="fc-property-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Loan Origination Date</label>
              <Input type="date" value={property.loan_origination_date || ''} onChange={e => setProperty(p => ({ ...p, loan_origination_date: e.target.value }))} data-testid="fc-loan-date" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Current Loan Status</label>
              <Input value={property.loan_status || ''} onChange={e => setProperty(p => ({ ...p, loan_status: e.target.value }))} placeholder="e.g., In default" data-testid="fc-loan-status" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Occupancy</label>
              <select className="w-full border rounded-md px-3 py-2 text-sm" value={property.occupancy || 'Primary residence'} onChange={e => setProperty(p => ({ ...p, occupancy: e.target.value }))} data-testid="fc-occupancy">
                <option>Primary residence</option>
                <option>Investment property</option>
                <option>Second home</option>
                <option>Other</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Parties Involved</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Borrower(s)</label>
              <Input value={parties.borrower || ''} onChange={e => setParties(p => ({ ...p, borrower: e.target.value }))} placeholder="Your full name(s)" data-testid="fc-borrower" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Loan Servicer</label>
              <Input value={parties.servicer || ''} onChange={e => setParties(p => ({ ...p, servicer: e.target.value }))} placeholder="Current servicer" data-testid="fc-servicer" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Lender</label>
              <Input value={parties.lender || ''} onChange={e => setParties(p => ({ ...p, lender: e.target.value }))} placeholder="Original or current lender" data-testid="fc-lender" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Trustee</label>
              <Input value={parties.trustee || ''} onChange={e => setParties(p => ({ ...p, trustee: e.target.value }))} placeholder="Trustee name" data-testid="fc-trustee" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Substitute Trustee</label>
              <Input value={parties.substitute_trustee || ''} onChange={e => setParties(p => ({ ...p, substitute_trustee: e.target.value }))} placeholder="If applicable" data-testid="fc-sub-trustee" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Law Firm</label>
              <Input value={parties.law_firm || ''} onChange={e => setParties(p => ({ ...p, law_firm: e.target.value }))} placeholder="Opposing law firm" data-testid="fc-law-firm" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Collection Companies</label>
            <Input value={parties.collection_companies || ''} onChange={e => setParties(p => ({ ...p, collection_companies: e.target.value }))} placeholder="Any collection agencies involved" data-testid="fc-collection" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Current Stage</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {CASE_STAGES.map(stage => (
              <button key={stage} onClick={() => setCaseStage(stage)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${caseStage === stage ? 'border-emerald-500 bg-emerald-50 text-emerald-800 font-medium' : 'border-gray-200 hover:border-emerald-300 text-gray-700'}`}
                data-testid={`fc-stage-${stage.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {stage}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Upload className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900">Upload Foreclosure Documents</h3>
            <p className="text-sm text-blue-700 mt-1">
              Upload your mortgage, deed of trust, notices, correspondence, court filings, and any other relevant documents. Categorize each for better AI analysis.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Document Category</label>
              <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} data-testid="fc-doc-category">
                {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="relative cursor-pointer">
                <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" disabled={uploading} data-testid="fc-doc-upload-input" />
                <Button variant="outline" className="pointer-events-none" disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {documents.length > 0 ? (
        <div className="space-y-2">
          <h3 className="font-medium text-gray-800">Uploaded Documents ({documents.length})</h3>
          {documents.map(doc => (
            <Card key={doc.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-xs">{doc.category}</Badge>
                      <span className="text-xs text-gray-500">{(doc.size / 1024).toFixed(0)} KB</span>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc.id)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Upload className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No documents uploaded yet</p>
          <p className="text-sm mt-1">Upload your foreclosure-related documents to enable AI analysis</p>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Brain className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-purple-900">AI Case Analysis</h3>
            <p className="text-sm text-purple-700 mt-1">
              Run a comprehensive AI analysis or select specific analysis types. The AI will review your case details and documents to provide insights.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {ANALYSIS_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setAnalysisTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${analysisTab === tab.key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid={`fc-analysis-tab-${tab.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">{ANALYSIS_TABS.find(t => t.key === analysisTab)?.label}</h3>
            <Button onClick={() => runAnalysis(analysisTab)} disabled={analyzing} className="bg-purple-600 hover:bg-purple-700" data-testid="fc-run-analysis">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {analyzing ? 'Analyzing...' : analysisResults[analysisTab] ? 'Re-analyze' : 'Run Analysis'}
            </Button>
          </div>

          {analysisResults[analysisTab] ? (
            <div className="bg-gray-50 rounded-lg p-4 max-h-[500px] overflow-y-auto">
              <p className="text-xs text-gray-500 mb-3">Generated: {new Date(analysisResults[analysisTab].generated_at).toLocaleString()}</p>
              <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {analysisResults[analysisTab].content}
              </div>
            </div>
          ) : analyzing ? (
            <div className="text-center py-16">
              <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-purple-500" />
              <p className="text-gray-600 font-medium">AI is analyzing your case...</p>
              <p className="text-sm text-gray-500 mt-1">This may take up to 60 seconds</p>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No analysis yet</p>
              <p className="text-sm mt-1">Click the button above to generate AI insights for this section</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileDown className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-teal-900">Foreclosure Document Generator</h3>
            <p className="text-sm text-teal-700 mt-1">
              Generate legal petitions customized with your case details, grounded in Equity Jurisprudence, English Common Law, the Magna Carta, and Constitutional principles.
            </p>
          </div>
        </div>
      </div>

      {/* Process instructions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-teal-600" />
          How This Process Works
        </h4>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">1</div>
            <div>
              <p className="font-medium text-gray-900">Start with Step 1 — Foreclosure Petition</p>
              <p className="text-gray-600 mt-0.5">Generate and mail the Petition to Produce the Original Promissory Note, Conditional Acceptance for Value, and Demand for Full Accounting. This demands the opposing party prove they hold the original note, demonstrate lawful standing, and provide a full accounting.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold">!</div>
            <div>
              <p className="font-medium text-gray-900">Wait for Their Response (30 Days)</p>
              <p className="text-gray-600 mt-0.5">If the opposing party <strong>refuses or fails to produce the original note</strong> and <strong>cannot show cause</strong> within the 30-day deadline, their silence constitutes acquiescence and estoppel. At that point, you may <strong>file for dismissal</strong> of the foreclosure action based on their failure to substantiate their claim.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">2</div>
            <div>
              <p className="font-medium text-gray-900">Then File Step 2 — Petition to Quiet Title</p>
              <p className="text-gray-600 mt-0.5">After the opposing party fails to respond or cannot prove the note, generate the Petition to Quiet Title and file it with the court. This petition asks the court to declare clear title in your favor and remove any cloud on your property created by the unsubstantiated foreclosure claim.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Document type tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setDocGenTab('petition')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium border transition-colors ${docGenTab === 'petition' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
          data-testid="fc-tab-petition"
        >
          <Scale className="w-4 h-4" />
          Step 1: Foreclosure Petition
        </button>
        <button
          onClick={() => setDocGenTab('quiet_title')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium border transition-colors ${docGenTab === 'quiet_title' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
          data-testid="fc-tab-quiet-title"
        >
          <FileText className="w-4 h-4" />
          Step 2: Petition to Quiet Title
        </button>
      </div>

      {/* ═══ TAB 1: Original Foreclosure Petition ═══ */}
      {docGenTab === 'petition' && (
        <>
          <Card className="border-teal-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 text-lg mb-2">{FORECLOSURE_DOC_TITLE}</h3>
              <p className="text-sm text-gray-600 mb-1">This unified document combines three critical foreclosure defense instruments into one authoritative petition:</p>
              <ul className="text-sm text-gray-600 mb-5 space-y-1.5 ml-4">
                <li className="flex items-start gap-2"><Scale className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Petition to Produce the Original Promissory Note</strong> — Demands production of the original wet-ink note as evidence of standing</span></li>
                <li className="flex items-start gap-2"><Scale className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Conditional Acceptance for Value</strong> — Conditionally accepts the obligation subject to proof of lawful consideration and standing</span></li>
                <li className="flex items-start gap-2"><Scale className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Demand for Full Accounting</strong> — Demands a complete accounting of the loan from origination to present</span></li>
              </ul>

              {!generatedDoc ? (
                <Button
                  onClick={generateDocument}
                  disabled={generatingDoc}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5 text-base"
                  data-testid="fc-generate-petition"
                >
                  {generatingDoc ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />}
                  {generatingDoc ? 'Generating Document...' : 'Generate Foreclosure Petition'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-green-900">Document Ready</p>
                      <p className="text-sm text-green-700">{generatedDoc.filename}</p>
                    </div>
                    <Button onClick={() => downloadDoc(generatedDoc)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="fc-download-petition">
                      <Download className="w-4 h-4 mr-2" />
                      Download DOCX
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => { setGeneratedDoc(null); generateDocument(); }}
                    disabled={generatingDoc}
                    className="w-full"
                    data-testid="fc-regenerate-petition"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Regenerate Document
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mailing Instructions for petition */}
          {generatedDoc && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="pt-5">
                <div className="flex items-start gap-3 mb-4">
                  <Send className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                  <h3 className="font-semibold text-amber-900 text-base">First Mailing Instructions</h3>
                </div>
                <div className="space-y-4 text-sm text-amber-900">
                  <p>After downloading your petition, send it to <strong>both</strong> of the following parties via <strong>USPS Certified Mail with Green Return Receipt (PS Form 3811)</strong>:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-amber-700" />
                        <p className="font-semibold">1. Prosecuting Attorney</p>
                      </div>
                      <p className="text-amber-800 text-xs">The attorney or law firm handling the foreclosure action on behalf of the lender/servicer.</p>
                    </div>
                    <div className="bg-white border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Scale className="w-4 h-4 text-amber-700" />
                        <p className="font-semibold">2. Court Room Clerk</p>
                      </div>
                      <p className="text-amber-800 text-xs">The Clerk of the Court where the foreclosure case is filed or pending.</p>
                    </div>
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
                    <p className="font-semibold flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-700" /> Mailing Checklist:</p>
                    <ul className="space-y-1 text-amber-800 ml-6 list-disc">
                      <li>Print <strong>3 copies</strong> (1 for each recipient + 1 for your records)</li>
                      <li>Send each copy via <strong>USPS Certified Mail</strong> with tracking</li>
                      <li>Attach a <strong>Green Return Receipt card (PS Form 3811)</strong> to each envelope</li>
                      <li>Keep your <strong>Certified Mail receipts</strong> and tracking numbers</li>
                      <li>When the green cards are returned, <strong>save them</strong> as proof of delivery</li>
                    </ul>
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg p-3">
                    <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-700" /> After Mailing:</p>
                    <p className="text-amber-800 mt-1">
                      Once mailed, proceed to <strong>Step 2: Petition to Quiet Title</strong> to generate the next document. Upload any responses to the <strong>AI Attorney</strong> tab.
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => setDocGenTab('quiet_title')} data-testid="fc-go-to-qt-tab">
                        <FileText className="w-3.5 h-3.5 mr-1.5" />
                        Go to Quiet Title
                      </Button>
                      <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => setCurrentStep(5)} data-testid="fc-go-to-attorney">
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                        Go to AI Attorney
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {generatingDoc && (
            <div className="text-center py-4">
              <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-teal-500" />
              <p className="text-gray-600 text-sm">AI is drafting your petition... This may take up to 90 seconds.</p>
            </div>
          )}
        </>
      )}

      {/* ═══ TAB 2: Petition to Quiet Title ═══ */}
      {docGenTab === 'quiet_title' && (
        <>
          {/* Educational disclaimer */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-900 text-sm">Educational Drafting Tool</h4>
                <p className="text-xs text-blue-700 mt-1">
                  This module produces an editable educational draft. All materials are for review, verification, and customization. This tool does not determine ownership rights, guarantee outcomes, or replace legal representation.
                </p>
              </div>
            </div>
          </div>

          <Card className="border-teal-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 text-lg mb-2">{QUIET_TITLE_DOC_TITLE}</h3>
              <p className="text-sm text-gray-600 mb-1">The AI will analyze your uploaded foreclosure documents and generate a comprehensive petition that includes:</p>
              <ul className="text-sm text-gray-600 mb-5 space-y-1.5 ml-4">
                <li className="flex items-start gap-2"><Search className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Document Analysis Report</strong> — Status of each uploaded document (available, unavailable, incomplete, inconsistent)</span></li>
                <li className="flex items-start gap-2"><Brain className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Educational Analysis</strong> — Chain of title, recorded interests, mortgage & note analysis, timeline, evidence assessment</span></li>
                <li className="flex items-start gap-2"><Scale className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Draft Petition</strong> — Court caption, parties, property description, statement of facts, claims grounded in equity, common law, Magna Carta, and constitutional principles</span></li>
                <li className="flex items-start gap-2"><FileText className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /><span><strong>Educational Foundations</strong> — Plain-English explanations of the legal principles applied</span></li>
              </ul>

              {documents.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      <strong>Tip:</strong> Upload your foreclosure documents in Step 2 first. The AI uses your uploaded documents to create a more detailed and accurate petition.
                    </p>
                  </div>
                </div>
              )}

              {!generatedQT ? (
                <Button
                  onClick={generateQuietTitle}
                  disabled={generatingQT}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5 text-base"
                  data-testid="fc-generate-quiet-title"
                >
                  {generatingQT ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />}
                  {generatingQT ? 'Generating Petition to Quiet Title...' : 'Generate Petition to Quiet Title'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-green-900">Petition to Quiet Title Ready</p>
                      <p className="text-sm text-green-700">{generatedQT.filename}</p>
                    </div>
                    <Button onClick={() => downloadDoc(generatedQT)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="fc-download-quiet-title">
                      <Download className="w-4 h-4 mr-2" />
                      Download DOCX
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => { setGeneratedQT(null); generateQuietTitle(); }}
                    disabled={generatingQT}
                    className="w-full"
                    data-testid="fc-regenerate-quiet-title"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Regenerate Petition
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mailing Instructions for Quiet Title */}
          {generatedQT && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="pt-5">
                <div className="flex items-start gap-3 mb-4">
                  <Send className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                  <h3 className="font-semibold text-amber-900 text-base">Filing & Mailing Instructions</h3>
                </div>
                <div className="space-y-4 text-sm text-amber-900">
                  <p>After downloading and reviewing your Petition to Quiet Title, follow these steps:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-amber-700" />
                        <p className="font-semibold">1. File with the Court</p>
                      </div>
                      <p className="text-amber-800 text-xs">File the petition with the Clerk of the Court in the county where the property is located. Obtain a stamped copy for your records.</p>
                    </div>
                    <div className="bg-white border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Scale className="w-4 h-4 text-amber-700" />
                        <p className="font-semibold">2. Serve All Respondents</p>
                      </div>
                      <p className="text-amber-800 text-xs">Serve a copy on each named respondent (servicer, lender, trustee, law firm) via USPS Certified Mail with Green Return Receipt (PS Form 3811).</p>
                    </div>
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
                    <p className="font-semibold flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-700" /> Mailing Checklist:</p>
                    <ul className="space-y-1 text-amber-800 ml-6 list-disc">
                      <li>Print enough copies for each respondent + the court + your records</li>
                      <li>Send each copy via <strong>USPS Certified Mail</strong> with tracking</li>
                      <li>Attach a <strong>Green Return Receipt card (PS Form 3811)</strong></li>
                      <li>Keep your <strong>Certified Mail receipts</strong> and tracking numbers</li>
                      <li>When the green cards are returned, <strong>save them</strong> as proof of service</li>
                    </ul>
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg p-3">
                    <p className="font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-amber-700" /> Need Help Refining the Petition?</p>
                    <p className="text-amber-800 mt-1">
                      Use the <strong>AI Attorney</strong> tab to ask questions about the petition, request explanations of specific sections, or get help customizing the draft.
                    </p>
                    <Button variant="outline" size="sm" className="mt-2 border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => setCurrentStep(5)} data-testid="fc-go-to-attorney-from-qt">
                      <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                      Go to AI Attorney
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {generatingQT && (
            <div className="text-center py-4">
              <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-teal-500" />
              <p className="text-gray-600 text-sm">AI is analyzing your documents and drafting the petition... This may take up to 2 minutes.</p>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Chat file upload
  const [chatUploading, setChatUploading] = useState(false);
  const chatFileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null); // { filename, text }

  const handleChatFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeCase) return;
    setChatUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/foreclosure/cases/${activeCase.id}/extract-text`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setPendingFile({ filename: res.data.filename, text: res.data.text });
        toast({ title: 'Document Attached', description: `${res.data.filename} is ready. Type your question and send.` });
      }
    } catch {
      toast({ title: 'Upload Failed', description: 'Could not process the document.', variant: 'destructive' });
    } finally {
      setChatUploading(false);
      if (chatFileRef.current) chatFileRef.current.value = '';
      e.target.value = '';
    }
  };

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900">AI Attorney Mode</h3>
            <p className="text-sm text-amber-700 mt-1">
              Your dedicated foreclosure assistant. Ask questions, upload responses you have received, or get help analyzing documents and planning your next steps.
            </p>
          </div>
        </div>
      </div>

      <Card className="flex flex-col" style={{ minHeight: '450px' }}>
        <CardContent className="flex-1 flex flex-col pt-6">
          <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[400px]">
            {chatHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">Start a conversation</p>
                <p className="text-sm mt-1">Ask questions or upload any responses you have received to get AI analysis</p>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {['Analyze a response I received', 'What are my next steps?', 'Explain my mortgage terms', 'What documents am I missing?'].map(q => (
                    <button key={q} onClick={() => { setChatMessage(q); }} className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200 hover:bg-amber-100">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              chatHistory.map((msg, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-end">
                    <div className="bg-blue-600 text-white rounded-lg px-4 py-2 max-w-[80%]">
                      <p className="text-sm">{msg.user}</p>
                    </div>
                  </div>
                  {msg.assistant ? (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-800 rounded-lg px-4 py-2 max-w-[80%]">
                        <p className="text-sm whitespace-pre-wrap">{msg.assistant}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-lg px-4 py-2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pending file attachment badge */}
          {pendingFile && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg mb-2">
              <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800 truncate flex-1">{pendingFile.filename}</span>
              <button onClick={() => setPendingFile(null)} className="p-0.5 hover:bg-amber-100 rounded" data-testid="fc-chat-remove-file">
                <X className="w-3.5 h-3.5 text-amber-600" />
              </button>
            </div>
          )}

          <div className="flex gap-2 mt-auto">
            <input
              ref={chatFileRef}
              type="file"
              className="hidden"
              onChange={handleChatFileUpload}
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
              disabled={chatLoading || chatUploading}
              data-testid="fc-chat-upload-input"
            />
            <Button
              variant="outline"
              size="icon"
              className={`flex-shrink-0 h-10 w-10 ${chatUploading ? 'bg-amber-100 border-amber-300' : pendingFile ? 'bg-amber-50 border-amber-400' : 'hover:bg-amber-50 hover:border-amber-400'}`}
              onClick={() => chatFileRef.current?.click()}
              disabled={chatLoading || chatUploading}
              title={pendingFile ? 'Replace attached document' : 'Upload a document for AI analysis'}
              data-testid="fc-chat-upload-btn"
            >
              {chatUploading ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> : <Upload className="w-4 h-4 text-gray-600" />}
            </Button>
            <Input
              value={chatMessage}
              onChange={e => setChatMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder={pendingFile ? `Ask about ${pendingFile.filename}...` : 'Ask about your foreclosure case or upload a document...'}
              disabled={chatLoading || chatUploading}
              data-testid="fc-chat-input"
            />
            <Button onClick={sendChat} disabled={chatLoading || chatUploading || (!chatMessage.trim() && !pendingFile)} className="bg-amber-600 hover:bg-amber-700" data-testid="fc-chat-send">
              {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Home className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900">Case Dashboard & Audit Trail</h3>
            <p className="text-sm text-gray-600 mt-1">
              Overview of your case progress, generated documents, and complete audit trail of all actions taken.
            </p>
          </div>
        </div>
      </div>

      {/* Case Status */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="text-center">
          <CardContent className="pt-4 pb-3">
            <p className="text-2xl font-bold text-blue-600">{documents.length}</p>
            <p className="text-xs text-gray-500 mt-1">Documents</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-4 pb-3">
            <p className="text-2xl font-bold text-purple-600">{Object.keys(analysisResults).length}</p>
            <p className="text-xs text-gray-500 mt-1">Analyses</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-4 pb-3">
            <p className="text-2xl font-bold text-amber-600">{chatHistory.length}</p>
            <p className="text-xs text-gray-500 mt-1">Chat Messages</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-4 pb-3">
            <p className="text-2xl font-bold text-emerald-600">{(generatedDoc ? 1 : 0) + (activeCase?.generated_documents?.length || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Docs Generated</p>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Progress */}
      <Card>
        <CardHeader><CardTitle className="text-base">Workflow Progress</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-1">
            {['Intake', 'Documents', 'Analysis', 'Doc Gen', 'Attorney', 'Audit'].map((label, idx) => {
              const completed = (idx === 0 && property.address) ||
                (idx === 1 && documents.length > 0) ||
                (idx === 2 && Object.keys(analysisResults).length > 0) ||
                (idx === 3 && (generatedDoc || (activeCase?.generated_documents?.length || 0) > 0)) ||
                (idx === 4 && chatHistory.length > 0) ||
                (idx === 5);
              return (
                <React.Fragment key={label}>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${completed ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
                    {completed ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {label}
                  </div>
                  {idx < 5 && <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Audit Trail */}
      <Card>
        <CardHeader><CardTitle className="text-base">Audit Trail</CardTitle></CardHeader>
        <CardContent>
          {auditTrail.length > 0 ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {[...auditTrail].reverse().map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-800">{entry.action}</p>
                    <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-6">No audit entries yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  // ─── MAIN RENDER ───

  if (!activeCase) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="foreclosure-defender-page">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Home className="w-7 h-7 text-emerald-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Foreclosure Defender</h1>
          </div>
          <p className="text-gray-600 mb-4">
            Your AI-powered Foreclosure Defense & Equity Analysis Center. This portal helps you organize your foreclosure case documents, analyze your situation through the lens of Equity Jurisprudence, English Common Law, and the Magna Carta, and generate the legal petitions you need to defend your property.
          </p>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-4.5 h-4.5 text-emerald-600" />
              How to Use the Foreclosure Defender
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">1</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Case Intake</p>
                  <p className="text-xs text-gray-600 mt-0.5">Enter your property details, loan information, and identify all parties involved — the servicer, lender, trustee, and any law firms or collection companies.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">2</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Upload Documents</p>
                  <p className="text-xs text-gray-600 mt-0.5">Upload your mortgage, note, deed of trust, notices, correspondence, and any other documents related to your foreclosure. The more you upload, the stronger your analysis.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">3</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">AI Case Analysis</p>
                  <p className="text-xs text-gray-600 mt-0.5">Run AI-powered analysis on your case — get a full breakdown, executive summary, mortgage analysis, timeline, issue spotting, and equity analysis.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">4</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Generate Documents</p>
                  <p className="text-xs text-gray-600 mt-0.5">Generate your Foreclosure Petition (Step 1) demanding the opposing party prove the note. If they fail, generate a Petition to Quiet Title (Step 2) and file it with the court.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">5</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">AI Attorney</p>
                  <p className="text-xs text-gray-600 mt-0.5">Chat with your dedicated AI foreclosure assistant. Ask questions, upload responses you receive, and get help analyzing documents and planning your next steps.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">6</div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Dashboard & Audit Trail</p>
                  <p className="text-xs text-gray-600 mt-0.5">Track your progress, review generated documents, and maintain a complete audit trail of every action taken on your case.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Button onClick={createNew} className="mb-6 bg-emerald-600 hover:bg-emerald-700" data-testid="fc-create-new">
          <Plus className="w-4 h-4 mr-2" />
          Start New Foreclosure Matter
        </Button>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-600" /></div>
        ) : cases.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Home className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h2 className="text-lg font-medium mb-2">No foreclosure cases yet</h2>
            <p className="text-sm">Click the button above to begin organizing your case.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="font-semibold text-gray-800">Your Cases ({cases.length})</h2>
            {cases.map(c => (
              <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadCase(c.id)}>
                <CardContent className="py-4 px-5 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{c.property?.address || 'No address entered'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{c.case_stage || 'Intake'}</Badge>
                      <span className="text-xs text-gray-500">{c.documents?.length || 0} documents</span>
                      <span className="text-xs text-gray-500">Created {new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); deleteCase(c.id); }} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="foreclosure-defender-wizard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setActiveCase(null); setCurrentStep(1); }} data-testid="fc-back-to-list">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 truncate max-w-xs sm:max-w-md">
              {property.address || 'New Foreclosure Case'}
            </h1>
            <p className="text-xs text-gray-500">Case ID: {activeCase.id.slice(0, 8)}...</p>
          </div>
        </div>
        <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">{activeCase.status}</Badge>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isActive = currentStep === step.num;
          const isDone = currentStep > step.num;
          return (
            <React.Fragment key={step.num}>
              <button
                onClick={() => { if (currentStep === 1) saveIntake(); setCurrentStep(step.num); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${isActive ? 'bg-emerald-600 text-white shadow-sm' : isDone ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                data-testid={`fc-step-${step.num}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {step.label}
              </button>
              {idx < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      {renderStepContent()}

      {/* Navigation */}
      <div className="flex justify-between mt-6 pt-4 border-t">
        <Button variant="outline" onClick={goBack} disabled={currentStep === 1} data-testid="fc-prev-step">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        {currentStep < 6 && (
          <Button onClick={goNext} className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} data-testid="fc-next-step">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {currentStep === 1 ? 'Save & Continue' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
